import type { Gender } from './types'

/**
 * Стандарты ВОЗ «вес к возрасту», 0–24 месяца, параметры LMS по месяцам.
 *
 * Источник: WHO Child Growth Standards, weight-for-age, таблицы `wfa_boys_0-to-5_zscores`
 * и `wfa_girls_0-to-5_zscores` (столбцы L, M, S по целым месяцам). Между месяцами —
 * линейная интерполяция: для первых недель это грубее недельных таблиц ВОЗ, но
 * разница в коридоре — десятки граммов при ширине коридора в килограмм.
 *
 * Стандарты построены по доношенным детям на грудном вскармливании; для недоношенных
 * они не годятся (в приложении об этом сказано в справке).
 */

interface Lms {
  l: number
  m: number
  s: number
}

/** [месяц] → L, M (кг), S. */
const BOYS: ReadonlyArray<readonly [number, number, number]> = [
  [0.3487, 3.3464, 0.14602],
  [0.2297, 4.4709, 0.13395],
  [0.197, 5.5675, 0.12385],
  [0.1738, 6.3762, 0.11727],
  [0.1553, 7.0023, 0.11316],
  [0.1395, 7.5105, 0.1108],
  [0.1257, 7.934, 0.10958],
  [0.1134, 8.297, 0.10902],
  [0.1021, 8.6151, 0.10882],
  [0.0917, 8.9014, 0.10881],
  [0.082, 9.1649, 0.10891],
  [0.073, 9.4122, 0.10906],
  [0.0644, 9.6479, 0.10925],
  [0.0563, 9.8749, 0.10949],
  [0.0487, 10.0953, 0.10976],
  [0.0413, 10.3108, 0.11007],
  [0.0343, 10.5228, 0.11041],
  [0.0275, 10.7319, 0.11079],
  [0.0211, 10.9385, 0.11119],
  [0.0148, 11.143, 0.11164],
  [0.0087, 11.3462, 0.11211],
  [0.0029, 11.5486, 0.11261],
  [-0.0028, 11.7504, 0.11314],
  [-0.0083, 11.9514, 0.11369],
  [-0.0137, 12.1515, 0.11426],
]

const GIRLS: ReadonlyArray<readonly [number, number, number]> = [
  [0.3809, 3.2322, 0.14171],
  [0.1714, 4.1873, 0.13724],
  [0.0962, 5.1282, 0.13],
  [0.0402, 5.8458, 0.12619],
  [-0.005, 6.4237, 0.12402],
  [-0.043, 6.8985, 0.12274],
  [-0.0756, 7.297, 0.12204],
  [-0.1039, 7.6422, 0.12178],
  [-0.1288, 7.9487, 0.12181],
  [-0.1507, 8.2254, 0.12199],
  [-0.17, 8.48, 0.12223],
  [-0.1872, 8.7192, 0.12247],
  [-0.2024, 8.9481, 0.12268],
  [-0.2158, 9.1699, 0.12283],
  [-0.2278, 9.387, 0.12294],
  [-0.2384, 9.6008, 0.12299],
  [-0.2478, 9.8124, 0.12303],
  [-0.2562, 10.0226, 0.12306],
  [-0.2637, 10.2315, 0.12309],
  [-0.2703, 10.4393, 0.12315],
  [-0.2762, 10.6464, 0.12323],
  [-0.2815, 10.8534, 0.12335],
  [-0.2862, 11.0608, 0.1235],
  [-0.2903, 11.2688, 0.12369],
  [-0.2941, 11.4775, 0.1239],
]

const DAY = 24 * 3600 * 1000
/** Средний месяц ВОЗ: 365,25 / 12 дней. */
const MONTH_DAYS = 30.4375

/** Возраст в месяцах с дробью по дням жизни; за пределами таблицы — её края. */
export function ageMonths(birthMillis: number, at: number): number {
  return Math.min(24, Math.max(0, (at - birthMillis) / DAY / MONTH_DAYS))
}

/** Параметры L, M, S для возраста в месяцах (с интерполяцией между строками). */
export function lmsAt(gender: Gender, months: number): Lms {
  const table = gender === 'GIRL' ? GIRLS : BOYS
  const m = Math.min(24, Math.max(0, months))
  const lo = Math.floor(m)
  const hi = Math.min(24, lo + 1)
  const t = m - lo
  const a = table[lo]!
  const b = table[hi]!
  return { l: a[0] + (b[0] - a[0]) * t, m: a[1] + (b[1] - a[1]) * t, s: a[2] + (b[2] - a[2]) * t }
}

/** z-оценка веса по LMS: ((x/M)^L − 1) / (L·S). */
export function zScore(grams: number, lms: Lms): number {
  const x = grams / 1000 / lms.m
  return lms.l === 0 ? Math.log(x) / lms.s : (Math.pow(x, lms.l) - 1) / (lms.l * lms.s)
}

/** Вес в граммах для z-оценки: обратная формула LMS. */
export function weightAtZ(z: number, lms: Lms): number {
  const kg = lms.l === 0 ? lms.m * Math.exp(lms.s * z) : lms.m * Math.pow(1 + lms.l * lms.s * z, 1 / lms.l)
  return Math.round(kg * 1000)
}

/** Функция нормального распределения: доля ровесников легче этого веса. */
export function normalCdf(z: number): number {
  // Φ(z) = (1 + erf(z / √2)) / 2; erf — по Abramowitz–Stegun 7.1.26, точность около 1e-7
  const x = Math.abs(z) / Math.SQRT2
  const t = 1 / (1 + 0.3275911 * x)
  const poly = t * (0.254829592 + t * (-0.284496736 + t * (1.421413741 + t * (-1.453152027 + t * 1.061405429))))
  const erf = 1 - poly * Math.exp(-x * x)
  return 0.5 * (1 + (z < 0 ? -erf : erf))
}

/** Центиль веса (0–100) для возраста и пола. */
export function centile(grams: number, gender: Gender, birthMillis: number, at: number): number {
  return normalCdf(zScore(grams, lmsAt(gender, ageMonths(birthMillis, at)))) * 100
}

/** z для границ коридора 3–97 %. */
export const Z_P3 = -1.8808
export const Z_P97 = 1.8808

/** Коридор 3–97 % и середина, граммы, для возраста и пола. */
export function corridor(gender: Gender, birthMillis: number, at: number): { low: number; mid: number; high: number } {
  const lms = lmsAt(gender, ageMonths(birthMillis, at))
  return { low: weightAtZ(Z_P3, lms), mid: weightAtZ(0, lms), high: weightAtZ(Z_P97, lms) }
}
